import type { ResourceLibrarySitePageLink } from '../../../../graphql/pimcore/modules/resourceLibrary/types/resourceLibrarySitePageItems';

export interface ResolvedSitePageLink {
    href: string | null;
    isExternal: boolean;
    openInNewTab: boolean;
}

function appendAnchor(href: string, anchor: string | null): string {
    if (!anchor || anchor.trim().length === 0) {
        return href;
    }

    const normalized = anchor.startsWith('#') ? anchor : `#${anchor}`;
    return `${href}${normalized}`;
}

function appendParameters(href: string, parameters: string | null): string {
    if (!parameters || parameters.trim().length === 0) {
        return href;
    }

    const normalized = parameters.startsWith('?') ? parameters.slice(1) : parameters;
    if (!normalized) {
        return href;
    }

    const separator = href.includes('?') ? '&' : '?';
    return `${href}${separator}${normalized}`;
}

function buildHref(base: string, anchor: string | null, parameters: string | null): string {
    const withParams = appendParameters(base, parameters);
    return appendAnchor(withParams, anchor);
}

function stripOrigin(url: URL, currentOrigin?: string): string | null {
    if (!currentOrigin) {
        if (typeof window === 'undefined' || !window.location) {
            return null;
        }
        currentOrigin = window.location.origin;
    }

    if (url.origin !== currentOrigin) {
        return null;
    }

    const path = url.pathname || '/';
    const search = url.search || '';
    return `${path}${search}`;
}

export function resolveSitePageLink(
    link: ResourceLibrarySitePageLink,
    currentOrigin?: string
): ResolvedSitePageLink {
    if (link.kind === 'internal') {
        return {
            href: buildHref(link.route, link.anchor, link.parameters),
            isExternal: false,
            openInNewTab: link.openInNewTab,
        };
    }

    let externalUrl = link.url;
    let isExternal = true;
    const anchor = link.anchor;
    const parameters = link.parameters;

    try {
        const parsed = new URL(link.url);
        const internalPath = stripOrigin(parsed, currentOrigin);
        if (internalPath) {
            externalUrl = internalPath;
            isExternal = false;
        }
    } catch {
        // If URL constructor fails, fall back to the original string
    }

    return {
        href: buildHref(externalUrl, anchor, parameters),
        isExternal,
        openInNewTab: link.openInNewTab,
    };
}
